import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { SECTEURS, fmtDays, fmtMoney, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Calculator as CalcIcon, ArrowRight, Info } from "@phosphor-icons/react";

const DEFAULTS = {
  libelle: "Analyse personnelle",
  devise: "UM",
  salaire_mensuel_net: 30000,
  prelevements_obligatoires: 90000,
  dette_nationale: 1500000000000,
  population: 4500000,
  service_dette_annuel: 80000000000,
  interets_dette_annuels: 40000000000,
  budget_annuel: 600000000000,
  annees_carriere: 40,
  generations: 2,
  alloc_p: { sante: 50000000000, education: 70000000000, justice: 20000000000, infrastructure: 60000000000, autres: 30000000000 },
  alloc_a: { sante: 100000000000, education: 90000000000, justice: 40000000000, infrastructure: 80000000000, autres: 50000000000 },
};

export default function Calculator() {
  const nav = useNavigate();
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);

  const sj = form.salaire_mensuel_net / 30;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        libelle: form.libelle,
        devise: form.devise,
        salaire_mensuel_net: Number(form.salaire_mensuel_net),
        prelevements_obligatoires: Number(form.prelevements_obligatoires),
        dette_nationale: Number(form.dette_nationale),
        population: Number(form.population),
        service_dette_annuel: Number(form.service_dette_annuel),
        interets_dette_annuels: Number(form.interets_dette_annuels),
        budget_annuel: Number(form.budget_annuel),
        annees_carriere: Number(form.annees_carriere),
        generations: Number(form.generations),
        allocations_percues: form.alloc_p,
        allocations_attendues: form.alloc_a,
      };
      const r = await api.post("/calculate", payload);
      toast.success("Analyse vitaliste générée");
      nav(`/analyses/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur calcul");
    } finally { setLoading(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setAlloc = (kind, sect, v) => setForm({ ...form, [kind]: { ...form[kind], [sect]: Number(v) || 0 } });

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10 fade-up">
        <div className="label-mono">§ 02 · Vita-Engine</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Moteur de calcul vitaliste</h1>
        <p className="mt-3 text-[var(--muted)] max-w-2xl">Saisissez les données monétaires : le moteur les convertira en jours de vie selon les 11 formules V1—V7 et D1—D5.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-black/15 border border-black/15" data-testid="calc-form">
        {/* Bloc 1 — Identité */}
        <Section title="§ 01 · Identité de l'analyse">
          <Field label="Libellé" testid="calc-libelle">
            <input value={form.libelle} onChange={(e) => set("libelle", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-libelle" />
          </Field>
          <Field label="Devise" testid="calc-devise">
            <select value={form.devise} onChange={(e) => set("devise", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-devise">
              <option value="UM">Ouguiya (UM)</option>
              <option value="FCFA">Franc CFA (FCFA)</option>
              <option value="EUR">Euro (€)</option>
              <option value="USD">Dollar (USD)</option>
            </select>
          </Field>
          <Field label="Années de carrière" testid="calc-carriere" hint="Pour V4 — bilan existentiel">
            <input type="number" value={form.annees_carriere} onChange={(e) => set("annees_carriere", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-carriere" />
          </Field>
          <Field label="Générations" testid="calc-gen" hint="Pour D4 — charge intergénérationnelle">
            <input type="number" value={form.generations} onChange={(e) => set("generations", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-gen" />
          </Field>
        </Section>

        {/* Bloc 2 — Pression fiscale */}
        <Section title="§ 02 · Pression fiscale (V1—V7)">
          <Field label="Salaire mensuel net" testid="calc-salaire" hint={`Sj actuel = ${(sj).toLocaleString("fr-FR", {maximumFractionDigits:0})} ${form.devise}/jour`}>
            <input type="number" value={form.salaire_mensuel_net} onChange={(e) => set("salaire_mensuel_net", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-salaire" />
          </Field>
          <Field label="Prélèvements obligatoires (annuels)" testid="calc-T" hint="Total impôts + taxes + cotisations">
            <input type="number" value={form.prelevements_obligatoires} onChange={(e) => set("prelevements_obligatoires", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-T" />
          </Field>
        </Section>

        {/* Bloc 3 — Dette */}
        <Section title="§ 03 · Dette nationale (D1—D5)">
          <Field label="Dette nationale totale" testid="calc-dette">
            <input type="number" value={form.dette_nationale} onChange={(e) => set("dette_nationale", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-dette" />
          </Field>
          <Field label="Population du pays" testid="calc-pop">
            <input type="number" value={form.population} onChange={(e) => set("population", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-pop" />
          </Field>
          <Field label="Service de la dette (annuel)" testid="calc-service">
            <input type="number" value={form.service_dette_annuel} onChange={(e) => set("service_dette_annuel", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-service" />
          </Field>
          <Field label="Intérêts annuels de la dette" testid="calc-interets">
            <input type="number" value={form.interets_dette_annuels} onChange={(e) => set("interets_dette_annuels", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-interets" />
          </Field>
          <Field label="Budget annuel total" testid="calc-budget">
            <input type="number" value={form.budget_annuel} onChange={(e) => set("budget_annuel", e.target.value)}
              className="w-full border border-black px-3 py-2 bg-transparent" data-testid="calc-input-budget" />
          </Field>
        </Section>

        {/* Bloc 4-5 — SRV */}
        <div className="bg-[var(--paper)] p-6 lg:col-span-3">
          <div className="label-mono">§ 04 · Score de Retour Vitaliste (V5 / SRV)</div>
          <h3 className="font-serif text-2xl mt-1">Allocations sectorielles</h3>
          <p className="text-sm text-[var(--muted)] mt-2 max-w-2xl">Comparez les montants <strong>réellement perçus</strong> à ceux qu'un service de qualité <strong>nécessiterait</strong>. Le ratio détermine la qualité de la restitution vitaliste.</p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECTEURS.map((s) => (
              <div key={s.key} className="border border-black/15 p-4" data-testid={`sect-${s.key}`}>
                <div className="flex items-center justify-between">
                  <div className="font-serif text-lg font-medium">{s.label}</div>
                  <div className="font-mono text-xs text-[var(--muted)]">×{s.poids}</div>
                </div>
                <div className="mt-3 space-y-2">
                  <Field label="Perçu" testid={`alloc-p-${s.key}`} compact>
                    <input type="number" value={form.alloc_p[s.key] || 0} onChange={(e) => setAlloc("alloc_p", s.key, e.target.value)}
                      className="w-full border border-black/30 px-2 py-1.5 bg-transparent text-sm" data-testid={`input-alloc-p-${s.key}`} />
                  </Field>
                  <Field label="Attendu" testid={`alloc-a-${s.key}`} compact>
                    <input type="number" value={form.alloc_a[s.key] || 0} onChange={(e) => setAlloc("alloc_a", s.key, e.target.value)}
                      className="w-full border border-black/30 px-2 py-1.5 bg-transparent text-sm" data-testid={`input-alloc-a-${s.key}`} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[var(--paper)] p-6 lg:col-span-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-[var(--muted)]">
            <Info size={16} />
            Les données pré-remplies illustrent un cas didactique mauritanien. Modifiez selon votre contexte.
          </div>
          <button type="submit" disabled={loading} data-testid="calc-submit"
            className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-3 label-mono flex items-center gap-2 disabled:opacity-60">
            <CalcIcon size={16} weight="bold" />
            {loading ? "Calcul en cours..." : "Lancer l'analyse vitaliste"}
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-[var(--paper)] p-6">
      <div className="label-mono mb-5">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children, testid, compact }) {
  return (
    <label className="block" data-testid={`field-${testid}`}>
      <div className={`label-mono mb-1 ${compact ? "text-[9px]" : ""}`}>{label}</div>
      {children}
      {hint && <div className="text-xs text-[var(--muted)] mt-1 font-mono">{hint}</div>}
    </label>
  );
}
